export type TooltipShowcasePosition = 'top' | 'bottom' | 'left' | 'right'

// `bottom` ships nowhere today, but PrimeNG's flip chain lands there whenever a
// top-positioned tooltip runs out of room, so it belongs in the matrix.
export const TOOLTIP_SHOWCASE_POSITIONS: ReadonlyArray<{ key: TooltipShowcasePosition; label: string }> = [
  { key: 'top', label: 'top' },
  { key: 'bottom', label: 'bottom' },
  { key: 'left', label: 'left' },
  { key: 'right', label: 'right' },
]

export const TOOLTIP_SAMPLE_ARN = 'arn:aws:eks:eu-west-1:000000000000:cluster/MOCK-TEST-eks-cluster'

const TOOLTIP_SAMPLE_PARAGRAPH =
  'This application has realtime replication enabled. Real-time replication works for DynamoDB tables, EC2 ' +
  'instances, S3 buckets, and RDS DB clusters. Other resources will be replicated via frequent snapshots.'

// Mirrors getAdditionalRolesTooltip() in user-management: a .tooltip-badges-container
// of inline-flex pills, rendered through [escape]="false".
export function buildTooltipBadgeHtml(count: number): string {
  const badges = Array.from(
    { length: count },
    (_, i) => `<div class="tooltip-badge">Role name that runs a while ${i + 1}</div>`
  ).join('')
  return `<div class="tooltip-badges-container">${badges}</div>`
}

export interface TooltipShowcaseVariant {
  key: string
  label: string
  // '' renders the production default (no tooltipStyleClass at the call site).
  styleClass: string
  cap: string
  escape: boolean
  content: string
  note: string
  // Mirrors each variant's production call site. autoHide: false is what lets the cursor
  // enter the bubble — required for the scrollable variants, since PrimeNG otherwise sets
  // pointer-events: none and hides on the host's mouseleave.
  autoHide: boolean
}

export const TOOLTIP_SHOWCASE_VARIANTS: ReadonlyArray<TooltipShowcaseVariant> = [
  {
    key: 'default',
    label: 'default (no class)',
    styleClass: '',
    cap: 'wraps at 520px, or the viewport if that is narrower',
    escape: true,
    content: 'Retry backup',
    note: 'The 67-usage majority: short label, one line, bubble is as wide as its text.',
    autoHide: true,
  },
  {
    key: 'arp-tooltip',
    label: 'arp-tooltip',
    styleClass: 'arp-tooltip',
    cap: 'min(800px, 100vw - 2rem)',
    escape: true,
    content: `Selected resource\n${TOOLTIP_SAMPLE_ARN}\nIncluded by rule: production-eks`,
    note: 'pre-wrap, so newlines and indentation in the content survive.',
    autoHide: true,
  },
  {
    key: 'badge-tooltip',
    label: 'custom-scrollable-tooltip badge-tooltip',
    styleClass: 'custom-scrollable-tooltip badge-tooltip',
    cap: '350px, scrolls past 200px tall',
    escape: false,
    // Enough pills to overflow the 200px cap, so the scroll behavior is actually visible.
    content: buildTooltipBadgeHtml(12),
    note: 'Access Management role/key pills. Hoverable so the list can be scrolled.',
    autoHide: false,
  },
  {
    key: 'real-time-rpo-tooltip',
    label: 'real-time-rpo-tooltip',
    styleClass: 'real-time-rpo-tooltip',
    cap: '380px',
    escape: true,
    content: TOOLTIP_SAMPLE_PARAGRAPH,
    note: 'Left-aligned wrapping prose. Hoverable so the docs link inside stays clickable.',
    autoHide: false,
  },
]
